// src/features/services/ServicesShowcase.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ServiceShowcase.scss";

const API_BASE = "https://api.ainfotech.com.np";
const GAP_PX = 24; // must match SCSS gap

export default function ServicesShowcase() {
  const [perView, setPerView] = useState(4);
  const [stepPx, setStepPx] = useState(0);
  const [items, setItems] = useState([]);     // fetched services
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const viewportRef = useRef(null);

  // Infinite slider state
  const [idx, setIdx] = useState(0);
  const [animate, setAnimate] = useState(true);

  const navigate = useNavigate();
  const openDetail = (key) => navigate(`/services/${key}`);

  // Fetch services from API
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axios.get(`${API_BASE}/api/services?page=1&limit=1000`, {
          withCredentials: true,
        });
        const apiItems = Array.isArray(res.data?.items) ? res.data.items : [];
        const mapped = apiItems.map((s) => ({
          id: s.id,
          key: s.slug,
          title: s.title,
          desc: s.excerpt,
          img: s.cover_image ? `${API_BASE}${s.cover_image}` : null,
        }));
        if (!cancel) setItems(mapped);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.error || e.message || "Failed to load services");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => { cancel = true; };
  }, []);

  // Responsive cards per view
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1200) setPerView(4);
      else if (w >= 992) setPerView(3);
      else if (w >= 576) setPerView(2);
      else setPerView(1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // How many clones we need on each side (keep at least perView)
  const CLONE = useMemo(
    () => (items.length ? Math.min(Math.max(perView, 1), items.length) : 0),
    [perView, items.length]
  );

  // Extended list with head/tail clones so we can loop seamlessly
  const EXTENDED = useMemo(() => {
    if (!items.length) return [];
    const head = items.slice(-CLONE);
    const tail = items.slice(0, CLONE);
    return [...head, ...items, ...tail];
  }, [items, CLONE]);

  // Start in the "real" region (after head clones)
  useEffect(() => {
    if (CLONE > 0) setIdx(CLONE);
  }, [CLONE]);

  // Measure one card width (add the gap so we slide exactly one card)
  const measureStep = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    const firstCard = vp.querySelector(".avena-svcCard");
    if (!firstCard) return;
    const rect = firstCard.getBoundingClientRect();
    setStepPx(rect.width + GAP_PX);
  };

  useEffect(() => {
    measureStep();
    const onResize = () => measureStep();
    window.addEventListener("resize", onResize);
    const r = requestAnimationFrame(measureStep);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(r);
    };
  }, [perView, EXTENDED.length]);

  // Move by exactly one card
  const go = (dir) => {
    if (!EXTENDED.length) return;
    setAnimate(true);
    setIdx((i) => i + (dir > 0 ? 1 : -1));
  };

  // After each animated slide, if we’re in the clone-zone, jump back without animation
  const onTransitionEnd = () => {
    if (!items.length) return;
    const realStart = CLONE;
    const realEnd = CLONE + items.length - 1;

    if (idx > realEnd) {
      setAnimate(false);
      setIdx(realStart);
    } else if (idx < realStart) {
      setAnimate(false);
      setIdx(realEnd);
    }
  };

  // Re-enable animation after a snap
  useEffect(() => {
    if (!animate) {
      const t = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(t);
    }
  }, [animate]);

  // Touch swipe
  const touch = useRef({ x: 0, dragging: false });
  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, dragging: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.dragging) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    touch.current.dragging = false;
    const threshold = 50;
    if (dx > threshold) go(-1);
    else if (dx < -threshold) go(1);
  };

  return (
    <section className="avena-svcs" id="services">
      <div className="container">
        <div className="avena-svcs__head">
          <div>
            <h2 className="avena-svcs__title">
              Explore Our <span>Services</span>
            </h2>
            <p className="avena-svcs__subtitle">
              We plan, deploy, and support complete solutions—software, networks, and
              devices—so your operations stay fast, visible, and secure.
            </p>
          </div>

          <div className="avena-svcs__nav">
            <button className="navbtn" aria-label="Previous" onClick={() => go(-1)}>
              <ArrowLeftIcon />
            </button>
            <button className="navbtn" aria-label="Next" onClick={() => go(1)}>
              <ArrowRightIcon />
            </button>
          </div>
        </div>

        {/* States */}
        {loading && (
          <div style={{ padding: "12px 0" }}>Loading services…</div>
        )}
        {err && !loading && (
          <div style={{ padding: "12px 0", color: "crimson" }}>{err}</div>
        )}
        {!loading && !err && items.length === 0 && (
          <div style={{ padding: "12px 0" }}>No services found.</div>
        )}

        {/* Slider */}
        {!loading && !err && items.length > 0 && (
          <div
            className="avena-svcs__viewport"
            ref={viewportRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{ "--perView": perView }}
          >
            <div
              className={`avena-svcs__track ${animate ? "is-animating" : ""}`}
              style={{ transform: `translateX(-${idx * stepPx}px)` }}
              onTransitionEnd={onTransitionEnd}
            >
              {EXTENDED.map((s, i) => (
                <article className="avena-svcCard" key={`${s.key}-${i}`}>
                  <button
                    type="button"
                    className="avena-svcCard__link"
                    onClick={() => openDetail(s.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDetail(s.key);
                      }
                    }}
                    aria-label={`${s.title} — view details`}
                  >
                    <img
                      className="avena-svcCard__img"
                      src={s.img || "/placeholder-service.png"}
                      alt={s.title}
                      loading="lazy"
                    />
                    <div className="avena-svcCard__overlay" />
                    <div className="avena-svcCard__meta">
                      <h3 className="avena-svcCard__title">{s.title}</h3>
                      <span className="avena-svcCard__round" aria-hidden="true">
                        <ArrowRightIcon />
                      </span>
                    </div>
                    <p className="avena-svcCard__desc">{s.desc}</p>
                  </button>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* icons */
function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function ArrowLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M19 12H5M11 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
